<script lang="ts">
    import FormErrors from '$components/Form/FormErrors.svelte';
    import HtmlForm, {
        type OnSubmitSuccessParams,
    } from '$components/Form/HtmlForm.svelte';
    import Row from '$components/Form/Row.svelte';
    import SubmitButton from '$components/Form/SubmitButton.svelte';
    import {
        createOnSubmit,
        type Fields,
        type FormData,
    } from '$components/GoafricaContact/GoafricaContactFormData';
    import Input from '$components/Form/Input.svelte';
    import Textarea from '$components/Form/Textarea.svelte';
    import * as t from '$trans';
    import { getContainer } from '$components/Context/ContainerContext';
    import type { Form } from '@jmvdevelop/svelte-form';
    import type { CurrentUser_MeFragment } from '$gql/graphql';
    import { onMount } from 'svelte';
    import Select, { type Choice } from '$components/Form/Select.svelte';
    import BBCode from '$components/BBCode/BBCode.svelte';

    export let form: Form<FormData, string>;
    export let fields: Fields<FormData>;
    export let prefixId: string;
    export let withCompanyNameRequired: boolean = false;
    export let withPhoneRequired: boolean = false;
    export let withUserInfos: boolean = false;
    export let withSubject: boolean = false;
    export let messagePlaceholder: string | null = null;
    export let messageLabel: string | null = null;
    export let currentUser: CurrentUser_MeFragment | null;

    export let onSuccess: (params: OnSubmitSuccessParams<FormData, number>) => void;

    const onSubmit = createOnSubmit(getContainer());

    const { recaptchaHelper, lang } = getContainer();

    onMount(() => {
        let remove: (() => void) | null;
        remove = form.addReadonlyListener(() => {
            recaptchaHelper().loadAndReadyRecaptcha();
            remove?.();
            remove = null;
        });

        return () => remove?.();
    });

    const toOptions: readonly Choice<string>[] = [
        {
            value: 'general',
            key: 'general',
            label: t.page_contact.option__to_general({}, $lang),
        },
        {
            value: 'administration',
            key: 'administration',
            label: t.page_contact.option__to_administration({}, $lang),
        },
        {
            value: 'public_relations',
            key: 'public_relations',
            label: t.page_contact.option__to_public_relations({}, $lang),
        },
        {
            value: 'yellow_pages',
            key: 'yellow_pages',
            label: t.page_contact.option__to_yellow_pages({}, $lang),
        },
        {
            value: 'web_creation',
            key: 'web_creation',
            label: t.page_contact.option__to_web_creation({}, $lang),
        },
    ];

    const subjectOptions: readonly Choice<string>[] = [
        {
            value: 'information',
            key: 'information',
            label: t.page_contact.option__subject_information({}, $lang),
        },
        {
            value: 'appointment',
            key: 'appointment',
            label: t.page_contact.option__subject_appointment({}, $lang),
        },
    ];

    const reportTypeOptions: readonly Choice<string>[] = [
        { value: 'violent', key: 'violent', label: 'Violents' },
        { value: 'racist', key: 'racist', label: 'Racistes' },
        { value: 'defamatory', key: 'defamatory', label: 'Diffamatoires' },
        { value: 'adult', key: 'adult', label: 'Osés, adultes...' },
    ];

    $: subject = form.stores().fieldValue(fields.subject);
</script>

<HtmlForm
    class="flex flex-col gap-y-[15px]"
    {form}
    {onSubmit}
    onSubmitSuccess={onSuccess}
>
    {#if withSubject}
        <div class="grid grid-cols-1 ls:grid-cols-2 gap-8">
            <Row
                {form}
                field={fields.to}
                label={t.page_contact.label_to({}, $lang)}
                required
                {prefixId}
                let:field
                let:id
            >
                <Select
                    {form}
                    {field}
                    {id}
                    options={toOptions}
                    required={false}
                    showChevron
                    placeholder={t.page_contact.placeholder_select({}, $lang)}
                />
            </Row>

            <Row
                {form}
                field={fields.subject}
                label={t.page_contact.label_subject({}, $lang)}
                required
                {prefixId}
                let:field
                let:id
            >
                <Select
                    {form}
                    {field}
                    {id}
                    options={subjectOptions}
                    required={false}
                    showChevron
                    placeholder={t.page_contact.placeholder_select({}, $lang)}
                />
            </Row>
        </div>
    {/if}

    {#if currentUser == null || withUserInfos}
        <div class="grid grid-cols-1 ls:grid-cols-2 gap-8">
            <Row
                {form}
                field={fields.firstname}
                label={t.page_contact.label_firstname({}, $lang)}
                required
                {prefixId}
                let:field
                let:id
            >
                <Input {form} {field} {id} toText={(id) => id} fromText={(id) => id} />
            </Row>

            <Row
                {form}
                field={fields.lastname}
                label={t.page_contact.label_lastname({}, $lang)}
                required
                {prefixId}
                let:field
                let:id
            >
                <Input {form} {field} {id} toText={(id) => id} fromText={(id) => id} />
            </Row>
        </div>
    {/if}

    <div class="grid grid-cols-1 ls:grid-cols-2 gap-8">
        {#if currentUser == null || withUserInfos}
            <Row
                {form}
                field={fields.email}
                label={t.page_contact.label_email({}, $lang)}
                required
                {prefixId}
                let:field
                let:id
            >
                <Input
                    {form}
                    {field}
                    {id}
                    type="email"
                    toText={(id) => id}
                    fromText={(id) => id}
                />
            </Row>
        {/if}

        <Row
            {form}
            field={fields.phone}
            label={t.page_contact.label_phone({}, $lang)}
            required={withPhoneRequired}
            {prefixId}
            let:field
            let:id
        >
            <Input {form} {field} {id} toText={(id) => id} fromText={(id) => id} />
        </Row>
    </div>

    {#if withCompanyNameRequired}
        <Row
            {form}
            field={fields.companyName}
            label={t.page_contact.label_companyName({}, $lang)}
            required
            {prefixId}
            let:field
            let:id
        >
            <Input {form} {field} {id} toText={(id) => id} fromText={(id) => id} />
        </Row>
    {/if}

    {#if $subject === 'report_feed_post'}
        <Row
            {form}
            field={fields.reportType}
            label={`Vous considérez les propos du post comme :`}
            required
            {prefixId}
            let:field
            let:id
        >
            <Select
                {form}
                {field}
                {id}
                options={reportTypeOptions}
                required={false}
                showChevron
            />
        </Row>
    {/if}

    <Row
        {form}
        field={fields.message}
        label={messageLabel ?? t.page_contact.label_message({}, $lang)}
        helpLabel={messagePlaceholder ?? null}
        required
        {prefixId}
        let:field
        let:id
    >
        <Textarea
            {form}
            {field}
            {id}
            rows={8}
            toText={(id) => id}
            fromText={(id) => id}
            placeholder={messagePlaceholder ?? ''}
        />
    </Row>

    <div class="flex flex-col gap-y-8">
        <FormErrors {form} />
    </div>

    <SubmitButton {form} class="btn-leo btn-leo-primary btn-xl">
        <i class="fa fa-paper-plane-o mr-3" aria-hidden={true} />
        <span>{t.ui.submit({}, $lang)}</span>
    </SubmitButton>

    <p class="text-gray-600 text-13">
        <BBCode bbcode={t.ui.form_data_processing_notice({}, $lang)} />
    </p>
</HtmlForm>
